import React, { useMemo } from "react";
import { isEmpty } from "lodash";
import { Theme, useTheme } from "@emotion/react";

import useSetState from "core/hooks/useSetState";
import useBreakpoint from "core/hooks/useBreakpoint";
import SectionContainer from "core/Components/SectionContainer";
import Image from "core/Components/Image";
import VideoButton from "core/Components/Buttons/VideoButton";
import VideoViewContainer from "core/Components/Video/VideoViewContainer";
import VideoViewLayer from "core/Components/Video/VideoViewLayer";
import VideoPlayer from "core/Components/Video/VideoPlayer";

import Wrapper from "elements/Components/BannerWithFullImage/components/Wrapper";
import ButtonContainer from "elements/Components/BannerWithFullImage/components/ButtonContainer";
import Header from "elements/Components/BannerWithFullImage/components/Header";
import HeaderAndButtonsContainer from "elements/Components/BannerWithFullImage/components/HeaderAndButtonsContainer";
import FirstButton from "elements/Components/BannerWithFullImage/components/FirstButton";
import SecondButton from "elements/Components/BannerWithFullImage/components/SecondButton";
import Text from "elements/Components/BannerWithFullImage/components/Text";
import { isOnly, isUp } from "theme/breakpoints";

type Props = {
    data: BannerWithFullImage,
    className?: string,
    isUsingAltDesign?: boolean,
};

type DefaultState = {
    videoPath: string,
}

const INITIAL_STATE = {
    videoPath: "",
}

const BannerWithFullImage: React.FC<Props> = ({ data, className = "", isUsingAltDesign = false }) => {
    const theme: Theme = useTheme();
    const { minWidth } = useBreakpoint();
    const colourTheme = JSON.parse(data.theme);
    const { breakpoint } = useBreakpoint();

    const { secondButtonTextColour, secondButtonBorderColour, } = colourTheme;
    const [state, setState] = useSetState<DefaultState>(INITIAL_STATE);

    const image = useMemo(() => {
        switch (true) {
            case isUp(breakpoint, '3xl'):
                return data.image || data.portraitImage;
            case isOnly(breakpoint, 'xxl'):
                return data.image || data.portraitImage;
            case isOnly(breakpoint, 'xl'):
                return data.image || data.portraitImage;
            case isOnly(breakpoint, 'lg'):
                return data.image || data.portraitImage;
            case isOnly(breakpoint, 'md'):
                return data.portraitImage || data.image;
            case isOnly(breakpoint, 'sm'):
                return data.portraitImage || data.image;
            default:
                return data.portraitImage || data.image;
        }
    }, [breakpoint, data]);

    const topBannerHeight = useMemo(() => {
        const hasPortraitImage = !isEmpty(data.portraitImage);
        switch (true) {
            case minWidth >= theme.breakpoints.sizes["5xl"]:
                return 888;
            case minWidth >= theme.breakpoints.sizes["3xl"]:
                return 619;
            case minWidth >= theme.breakpoints.sizes["xxl"]:
                return 560;
            case minWidth >= theme.breakpoints.sizes["xl"]:
                return 489;
            case minWidth >= theme.breakpoints.sizes["lg"]:
                return 490;
            case minWidth >= theme.breakpoints.sizes["md"]:
                return hasPortraitImage ? 560 : 831;
            case minWidth >= theme.breakpoints.sizes["sm"]:
                return hasPortraitImage ? 400 : 568;
            default:
                return hasPortraitImage ? 290 : 494;
        }
    }, [data.portraitImage, minWidth, theme.breakpoints.sizes]);

    const onClickVideoHandler = () => {
        setState({ videoPath: data.videoPath });
    };

    const onClearVideoHandler = () => {
        setState({ videoPath: "" });
    };

    return isEmpty(data.image) ? null : (
        <SectionContainer data={data} className={className} overflowHidden>
            <Wrapper
                className={"banner-with-full-image--wrapper"}
                topBannerHeight={topBannerHeight}
                isUsingAltDesign={isUsingAltDesign}
            >
                <Image
                    image={image}
                    useFocusPoint={data.useFocusPoint}
                />

                <HeaderAndButtonsContainer contentAlignment={data.contentAlignment}>
                    <Header data={data} isUsingAltDesign={isUsingAltDesign} />
                    <Text data={data} />

                    <ButtonContainer>
                        <FirstButton data={data} />
                        <SecondButton data={data} />

                        <VideoButton
                            data={data}
                            onClickVideoHandler={onClickVideoHandler}
                            borderColour={secondButtonBorderColour}
                            textColour={secondButtonTextColour}
                        />
                    </ButtonContainer>
                </HeaderAndButtonsContainer>

                {state.videoPath && (
                    <VideoViewContainer clearPlayVideo={onClearVideoHandler}>
                        <VideoViewLayer>
                            <VideoPlayer videoPath={state.videoPath} autoPlay />
                        </VideoViewLayer>
                    </VideoViewContainer>
                )}
            </Wrapper>
        </SectionContainer>
    );
}

export default BannerWithFullImage;
